/**
 * Trade Reports API service.
 *
 * Metadata calls (presign/confirm/list/view/delete) go through {@link apiClient}
 * with paths that omit the `/api` prefix, matching `ibkr-settings-service.ts`.
 *
 * The byte upload deliberately bypasses `apiClient`: it is a direct browser->S3
 * request to the presigned URL, with no Cognito header and no `/api` base. The
 * backend issues a presigned PUT (so `fields` is absent), but presigned POST
 * (with `fields`) is also supported for robustness.
 *
 * Validates: Requirements 2.1, 2.2, 2.5, 5.1, 6.1, 7.1
 */

import type {
  ConfirmUploadRequest,
  PresignUploadRequest,
  PresignUploadResponse,
  TradeReport,
  TradeReportListResponse,
  ViewUrlResponse,
} from '@covered-calls/shared';
import { apiClient } from './api-client';

/**
 * Request a presigned upload slot for a report on the given position.
 */
export async function requestReportUpload(
  positionId: string,
  req: PresignUploadRequest
): Promise<PresignUploadResponse> {
  return apiClient.post<PresignUploadResponse>(`/trades/${positionId}/reports`, req);
}

/**
 * Upload the PDF bytes directly to S3 using the presigned URL.
 *
 * This does NOT use `apiClient` — the bytes go straight to S3 with no Cognito
 * header. Two upload shapes are supported:
 * - Presigned POST: `presign.fields` is present; the file is sent as multipart
 *   form-data with each policy field appended before the `file` field.
 * - Presigned PUT: `presign.fields` is absent; the file is sent as the request
 *   body with a `Content-Type` header (the backend's primary path).
 */
export async function uploadReportToS3(
  presign: PresignUploadResponse,
  file: File
): Promise<void> {
  let response: Response;

  if (presign.fields) {
    const form = new FormData();
    Object.entries(presign.fields).forEach(([key, value]) => {
      form.append(key, value);
    });
    form.append('file', file);
    response = await fetch(presign.uploadUrl, { method: 'POST', body: form });
  } else {
    response = await fetch(presign.uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type || 'application/pdf' },
    });
  }

  if (!response.ok) {
    throw new Error(`Upload failed with status ${response.status}`);
  }
}

/**
 * Confirm an upload so the backend writes report metadata after the bytes are
 * in S3.
 */
export async function confirmReportUpload(
  positionId: string,
  reportId: string,
  req: ConfirmUploadRequest
): Promise<TradeReport> {
  return apiClient.post<TradeReport>(
    `/trades/${positionId}/reports/${reportId}/confirm`,
    req
  );
}

/**
 * List all reports attached to a position.
 */
export async function listReports(positionId: string): Promise<TradeReportListResponse> {
  return apiClient.get<TradeReportListResponse>(`/trades/${positionId}/reports`);
}

/**
 * Get a short-lived presigned GET URL for viewing a report.
 */
export async function getReportViewUrl(
  positionId: string,
  reportId: string
): Promise<ViewUrlResponse> {
  return apiClient.get<ViewUrlResponse>(`/trades/${positionId}/reports/${reportId}`);
}

/**
 * Delete a report (removes both the S3 object and the metadata item).
 */
export async function deleteReport(positionId: string, reportId: string): Promise<void> {
  await apiClient.delete(`/trades/${positionId}/reports/${reportId}`);
}
